import { Trash2 } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";
import Row from "react-bootstrap/Row";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import { buscarEnderecoPorCEP } from "../../services/externalApiService";
import {
  buscarEstados,
  buscarMunicipiosPorEstado,
  Estado,
  Municipio,
} from "../../services/ibgeService";
import { isCPFRegistered } from "../../services/userService";
import { ClienteFormData, ClienteFormProps } from "../../types";
import {
  formatCEP,
  formatCPF,
  formatPhone,
  validateCEP,
  validateCPF,
  validatePhone,
} from "../../utils/validation";
import CustomerAddress from "./Components/CustomerAddress";
import CustomerHistory from "./Components/CustomerHistory";
import "./ClienteForm.css";
import { useConfirmModal } from "../../hooks/useConfirmModal";
import ConfirmModal from "../Common/ConfirmModal";

const ClienteForm: React.FC<ClienteFormProps> = ({
  show,
  onHide,
  onSave,
  onDelete,
  cliente = null,
  customHeaderContent,
  embedded,
}) => {
  const [formData, setFormData] = useState<ClienteFormData>({
    nome: "",
    telefone: "",
    cpf: "",
    medidas: "",
    observacoes: "",
    endereco: {
      cep: "",
      logradouro: "",
      numero: "",
      complemento: "",
      bairro: "",
      cidade: "",
      uf: "",
    },
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [loadingCEP, setLoadingCEP] = useState<boolean>(false);
  const [estados, setEstados] = useState<Estado[]>([]);
  const [municipios, setMunicipios] = useState<Municipio[]>([]);
  const [loadingMunicipios, setLoadingMunicipios] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>("dados");
  const { modalState: cancelModalState, showConfirm: showCancelConfirm, hideConfirm: hideCancelConfirm, handleConfirm: handleCancelConfirm } = useConfirmModal();

  // Estados para busca de municípios
  const [municipioSearchTerm, setMunicipioSearchTerm] = useState<string>("");
  const [showMunicipioDropdown, setShowMunicipioDropdown] =
    useState<boolean>(false);
  const [selectedMunicipio, setSelectedMunicipio] = useState<Municipio | null>(
    null,
  );
  const municipioSearchRef = useRef<HTMLDivElement>(null);
  const isInitialLoadRef = useRef<boolean>(true);

  const handleDeleteClick = async () => {
    if (onDelete && cliente?.id) {
      await onDelete(cliente.id);
    }
  };

  const handleCancel = () => {
    showCancelConfirm(
      'Cancelar cadastro',
      'Tem certeza que deseja cancelar? Os dados preenchidos serão perdidos.',
      () => {
        hideCancelConfirm();
        onHide();
      },
      'danger',
      'Sim, cancelar',
      'Continuar editando'
    );
  };

  // Função para buscar endereço por CEP com debounce
  const buscarEndereco = useCallback(async (cep: string) => {
    if (!cep || cep.replace(/\D/g, "").length !== 8) return;

    setLoadingCEP(true);
    try {
      const endereco = await buscarEnderecoPorCEP(cep);
      if (endereco) {
        setFormData((prev) => ({
          ...prev,
          endereco: {
            ...prev.endereco!,
            logradouro: endereco.logradouro,
            bairro: endereco.bairro,
            cidade: endereco.cidade,
            uf: endereco.uf,
            complemento:
              endereco.complemento || prev.endereco?.complemento || "",
          },
        }));

        // Atualizar também os estados do sistema de busca de municípios
        setMunicipioSearchTerm(endereco.cidade);
        setSelectedMunicipio({
          id: 0, // ID temporário
          nome: endereco.cidade,
        });
      }
    } catch (error) {
      console.error("Erro ao buscar CEP:", error);
    } finally {
      setLoadingCEP(false);
    }
  }, []);

  // Função para carregar estados do IBGE
  const carregarEstados = useCallback(async () => {
    try {
      const estadosData = await buscarEstados();
      setEstados(estadosData);
    } catch (error) {
      console.error("Erro ao carregar estados:", error);
    }
  }, []);

  // Função para carregar municípios por estado
  const carregarMunicipios = useCallback(async (siglaUF: string) => {
    if (!siglaUF || siglaUF.length !== 2) {
      setMunicipios([]);
      return;
    }

    setLoadingMunicipios(true);
    try {
      const municipiosData = await buscarMunicipiosPorEstado(siglaUF);
      setMunicipios(municipiosData);
    } catch (error) {
      console.error("Erro ao carregar municípios:", error);
      setMunicipios([]);
    } finally {
      setLoadingMunicipios(false);
    }
  }, []);

  // Carregar estados quando o modal abrir
  useEffect(() => {
    if (show) {
      carregarEstados();
    }
  }, [show, carregarEstados]);

  // Carregar municípios quando o estado mudar
  useEffect(() => {
    if (formData.endereco?.uf) {
      carregarMunicipios(formData.endereco.uf);
    } else {
      setMunicipios([]);
    }
  }, [formData.endereco?.uf, carregarMunicipios]);

  // Gerenciar dropdown de municípios
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        municipioSearchRef.current &&
        !municipioSearchRef.current.contains(event.target as Node)
      ) {
        setShowMunicipioDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Filtrar municípios baseado no termo de busca
  const filteredMunicipios = municipios.filter((municipio) =>
    municipio.nome.toLowerCase().includes(municipioSearchTerm.toLowerCase()),
  );

  // Função para lidar com busca de municípios
  const handleMunicipioSearch = (term: string) => {
    setMunicipioSearchTerm(term);
    if (formData.endereco?.uf && !loadingMunicipios) {
      setShowMunicipioDropdown(true);
    }

    // Se o termo estiver vazio, limpar seleção
    if (!term.trim()) {
      setSelectedMunicipio(null);
      setFormData((prev) => ({
        ...prev,
        endereco: {
          ...prev.endereco!,
          cidade: "",
        },
      }));
    }
  };

  // Função para selecionar município
  const handleMunicipioSelect = (municipio: Municipio) => {
    setSelectedMunicipio(municipio);
    setMunicipioSearchTerm(municipio.nome);
    setShowMunicipioDropdown(false);
    setFormData((prev) => ({
      ...prev,
      endereco: {
        ...prev.endereco!,
        cidade: municipio.nome,
      },
    }));
  };

  // Função para limpar seleção de município
  const handleMunicipioClear = () => {
    setSelectedMunicipio(null);
    setMunicipioSearchTerm("");
    setFormData((prev) => ({
      ...prev,
      endereco: {
        ...prev.endereco!,
        cidade: "",
      },
    }));
  };

  // Debounce para busca de CEP
  useEffect(() => {
    // Se for carga inicial (editando cliente), não buscar CEP automaticamente
    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
      return;
    }

    const timer = setTimeout(() => {
      if (formData.endereco?.cep) {
        buscarEndereco(formData.endereco.cep);
      }
    }, 1000); // Aguarda 1 segundo após parar de digitar

    return () => clearTimeout(timer);
  }, [formData.endereco?.cep, buscarEndereco]);

  // Atualizar formData quando cliente muda (para edição)
  useEffect(() => {
    if (cliente) {
      setFormData({
        nome: cliente.nome || "",
        telefone: cliente.telefone || "",
        cpf: cliente.cpf || "",
        medidas: cliente.medidas || "",
        observacoes: cliente.observacoes || "",
        endereco: cliente.endereco || {
          cep: "",
          logradouro: "",
          numero: "",
          complemento: "",
          bairro: "",
          cidade: "",
          uf: "",
        },
      });
    } else {
      // Limpar formulário para novo cliente
      setFormData({
        nome: "",
        telefone: "",
        cpf: "",
        medidas: "",
        observacoes: "",
        endereco: {
          cep: "",
          logradouro: "",
          numero: "",
          complemento: "",
          bairro: "",
          cidade: "",
          uf: "",
        },
      });
    }

    // Configurar estados de busca de municípios
    if (cliente?.endereco?.cidade) {
      setMunicipioSearchTerm(cliente.endereco.cidade);
      setSelectedMunicipio({
        id: 0, // ID temporário
        nome: cliente.endereco.cidade,
      });
    } else {
      setMunicipioSearchTerm("");
      setSelectedMunicipio(null);
    }

    setError(""); // Limpar erros ao abrir modal
    isInitialLoadRef.current = true; // Resetar flag de carga inicial
  }, [cliente, show]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    let processedValue = value;

    // Verificar se é um campo de endereço
    if (name.startsWith("endereco.")) {
      const enderecoField = name.split(".")[1];

      if (enderecoField === "cep") {
        processedValue = formatCEP(value);
      } else if (enderecoField === "uf") {
        processedValue = value.toUpperCase().slice(0, 2);
        // Limpar cidade e busca de municípios quando estado mudar
        setMunicipioSearchTerm("");
        setSelectedMunicipio(null);
        setFormData((prev) => ({
          ...prev,
          endereco: {
            ...prev.endereco!,
            uf: processedValue,
            cidade: "",
          },
        }));
        return;
      } else if (enderecoField === "logradouro" || enderecoField === "bairro") {
        processedValue = value.toUpperCase();
      } else if (enderecoField === "cidade") {
        // Para cidade, manter o valor original (não converter para maiúscula)
        processedValue = value;
      }

      setFormData((prev) => ({
        ...prev,
        endereco: {
          ...prev.endereco!,
          [enderecoField]: processedValue,
        },
      }));
    } else {
      // Campos normais
      if (name === "cpf") processedValue = formatCPF(value);
      else if (name === "telefone") processedValue = formatPhone(value);
      else if (name === "nome" || name === "medidas")
        processedValue = value.toUpperCase();
      // else if (name === 'observacoes') processedValue = sanitizeText(value);

      setFormData((prev) => ({ ...prev, [name]: processedValue }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    // Validações
    if (!validateCPF(formData.cpf)) {
      setError("CPF inválido");
      setLoading(false);
      return;
    }

    if (!validatePhone(formData.telefone)) {
      setError("Telefone inválido");
      setLoading(false);
      return;
    }

    if (formData.nome.trim().length < 2) {
      setError("Nome deve ter pelo menos 2 caracteres");
      setLoading(false);
      return;
    }

    // Validação do CEP (se preenchido)
    if (formData.endereco?.cep && !validateCEP(formData.endereco.cep)) {
      setError("CEP inválido");
      setLoading(false);
      return;
    }

    try {
      const cleanCPF = formData.cpf.replace(/\D/g, ""); // Remove pontuação

      // Verificar se CPF já existe (ignorando o próprio cliente se estiver editando)
      const cpfExists = await isCPFRegistered(cleanCPF, cliente?.id);
      if (cpfExists) {
        setError("Este CPF já está cadastrado!");
        setLoading(false);
        return;
      }

      // Chamar onSave para que a lista seja atualizada
      await onSave({
        ...formData,
        cpf: cleanCPF,
        endereco: formData.endereco?.cep
          ? {
            ...formData.endereco,
            cep: formData.endereco.cep.replace(/\D/g, ""), // Remove pontuação do CEP
          }
          : undefined,
      });

      // Fechar modal apenas se não estiver no modo embedded (fluxo de múltiplos steps)
      if (!embedded) {
        onHide();
      }
    } catch (err: any) {
      setError(err.message || "Erro ao salvar cliente");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleCancel}
      size="xl"
      centered
      dialogClassName="modal-dialog-centered modal-xl"
      contentClassName="modal-content-centered"
    >
      <Modal.Header closeButton>
        <Modal.Title>{cliente ? "Cliente" : "Novo Cliente"}</Modal.Title>
      </Modal.Header>
      {customHeaderContent && (
        <div
          style={{
            padding: "15px 1rem",
            borderBottom: "1px solid #dee2e6",
            background: "#f8f9fa",
          }}
        >
          {customHeaderContent}
        </div>
      )}
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}

          {/* Tabs: Dados do Cliente e Histórico */}
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k || "dados")}
            className="mb-3"
          >
            {/* Aba de Dados do Cliente */}
            <Tab eventKey="dados" title="Dados do Cliente" >
              <Row>
                <Col xs={12} md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Nome *</Form.Label>
                    <Form.Control
                      type="text"
                      name="nome"
                      value={formData.nome}
                      onChange={handleChange}
                      required
                      placeholder="Nome completo"
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Telefone *</Form.Label>
                    <Form.Control
                      type="tel"
                      name="telefone"
                      value={formData.telefone}
                      onChange={handleChange}
                      required
                      placeholder="(11) 99999-9999"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col xs={12} md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>CPF *</Form.Label>
                    <Form.Control
                      type="text"
                      name="cpf"
                      value={formData.cpf}
                      onChange={handleChange}
                      required
                      placeholder="000.000.000-00"
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* Seção de Endereço */}
              <hr className="my-4" />
              <h6 className="mb-3 text-primary">Endereço (Opcional)</h6>

              <CustomerAddress
                formData={formData}
                handleChange={handleChange}
                loadingCEP={loadingCEP}
                loadingMunicipios={loadingMunicipios}
                estados={estados}
                municipioSearchTerm={municipioSearchTerm}
                showMunicipioDropdown={showMunicipioDropdown}
                selectedMunicipio={selectedMunicipio}
                filteredMunicipios={filteredMunicipios}
                municipioSearchRef={municipioSearchRef}
                handleMunicipioSearch={handleMunicipioSearch}
                handleMunicipioSelect={handleMunicipioSelect}
                handleMunicipioClear={handleMunicipioClear}
              />

              <hr className="my-4" />
              <Form.Group className="mb-3">
                <Form.Label>Observações</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="observacoes"
                  value={formData.observacoes}
                  onChange={handleChange}
                  placeholder="Observações adicionais sobre o cliente"
                />
              </Form.Group>
            </Tab>

            {/* Aba de Histórico de Reservas
            {cliente?.id && (
              <Tab eventKey="historico" title="Histórico de Reservas">
                <CustomerHistory clienteId={cliente.id} />
              </Tab>
            )} */}
          </Tabs>

          {/* Campos fora das tabs removidos */}
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex justify-content-end w-100 align-items-center gap-3">
            {/* Botão de excluir à esquerda (apenas ao editar) */}
            <Button variant="secondary" onClick={handleCancel}>
              Cancelar
            </Button>

            {cliente && onDelete && (
              <Button
                variant="danger"
                onClick={handleDeleteClick}
                className="d-flex align-items-center gap-2"
              >
                <Trash2 size={16} />
                Excluir Cliente
              </Button>
            )}
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? "Salvando..." : "Salvar"}
            </Button>
          </div>
        </Modal.Footer>
      </Form>
      <ConfirmModal
        show={cancelModalState.show}
        onHide={hideCancelConfirm}
        onConfirm={handleCancelConfirm}
        title={cancelModalState.title}
        message={cancelModalState.message}
        type={cancelModalState.type}
        confirmText={cancelModalState.confirmText}
        cancelText={cancelModalState.cancelText}
        loading={cancelModalState.loading}
      />
    </Modal>
  );
};

export default ClienteForm;
